// LoansInfo.js - Update the import section
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  MoneyOff,
  AccountBalance,
  Visibility,
  Delete
} from '@mui/icons-material';
import { fetchMemberLoans, approveLoan, rejectLoan, disburseLoan, cancelLoan } from './api';

// Update the LoansInfo component
const LoansInfo = ({ userId }) => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentLoan, setCurrentLoan] = useState(null);
  const [dialogAction, setDialogAction] = useState('');
  const [notes, setNotes] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const loadLoans = async () => {
      try {
        setLoading(true);
        const data = await fetchMemberLoans(userId);
        setLoans(data);
      } catch (error) {
        console.error('Error loading loans:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLoans();
  }, [userId]);

  const handleOpenDialog = (loan, action) => {
    setCurrentLoan(loan);
    setDialogAction(action);
    setNotes('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentLoan(null);
    setDialogAction('');
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAction = async () => {
    try {
      const data = {
        loan_id: currentLoan.id,
        notes: notes
      };

      let response;
      switch (dialogAction) {
        case 'approve':
          response = await approveLoan(data);
          break;
        case 'reject':
          response = await rejectLoan(data);
          break;
        case 'disburse':
          response = await disburseLoan(data);
          break;
        case 'cancel':
          response = await cancelLoan(data);
          break;
        default:
          break;
      }

      // Refresh loans data
      const updatedLoans = await fetchMemberLoans(userId);
      setLoans(updatedLoans);
      handleCloseDialog();
    } catch (error) {
      console.error('Error processing loan action:', error);
      alert('Error processing request. Please try again.');
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'approved':
        return <Chip label="Approved" color="info" size="small" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" />;
      case 'disbursed':
        return <Chip label="Disbursed" color="success" size="small" />;
      case 'repaid':
        return <Chip label="Repaid" color="primary" size="small" />;
      case 'cancelled':
        return <Chip label="Cancelled" color="default" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getDialogTitle = () => {
    switch (dialogAction) {
      case 'approve':
        return 'Approve Loan Application';
      case 'reject':
        return 'Reject Loan Application';
      case 'disburse':
        return 'Disburse Loan';
      case 'cancel':
        return 'Cancel Loan Application';
      default:
        return 'Loan Action';
    }
  };

  const filteredLoans = () => {
    switch (tabValue) {
      case 0: // All loans
        return loans;
      case 1: // Pending loans
        return loans.filter(loan => loan.status === 'pending');
      case 2: // Approved & Disbursed loans
        return loans.filter(loan => ['approved', 'disbursed'].includes(loan.status));
      case 3: // Rejected & Cancelled loans
        return loans.filter(loan => ['rejected', 'cancelled'].includes(loan.status));
      default:
        return loans;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loans.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No loan applications found
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="loan status tabs">
          <Tab label="All Loans" />
          <Tab label="Pending" />
          <Tab label="Approved & Disbursed" />
          <Tab label="Rejected & Cancelled" />
        </Tabs>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Loan ID</TableCell> {/* NEW: Added Loan ID column */}
              <TableCell>Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Installments</TableCell>
              <TableCell>Monthly Payment</TableCell>
              <TableCell>Total Repayment</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Officer</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLoans().map((loan) => (
              <TableRow key={loan.id}>
                <TableCell> {/* NEW: Display Loan ID */}
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    #{loan.id}
                  </Typography>
                </TableCell>
                <TableCell>{new Date(loan.created_at).toLocaleDateString()}</TableCell>
                <TableCell>KES {loan.amount.toLocaleString()}</TableCell>
                <TableCell>{loan.installments}</TableCell>
                <TableCell>KES {loan.installment_amount ? loan.installment_amount.toLocaleString() : '-'}</TableCell>
                <TableCell>KES {loan.total_repayment ? loan.total_repayment.toLocaleString() : '-'}</TableCell>
                <TableCell>{getStatusChip(loan.status)}</TableCell>
                <TableCell>{loan.officer_name}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {loan.status === 'pending' && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => handleOpenDialog(loan, 'approve')}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => handleOpenDialog(loan, 'reject')}
                        >
                          Reject
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<Delete />}
                          onClick={() => handleOpenDialog(loan, 'cancel')}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    {loan.status === 'approved' && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<AccountBalance />}
                          onClick={() => handleOpenDialog(loan, 'disburse')}
                        >
                          Disburse
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<Delete />}
                          onClick={() => handleOpenDialog(loan, 'cancel')}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => window.open(loan.document_url, '_blank')}
                      disabled={!loan.document_url}
                    >
                      View Doc
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          {currentLoan && (
            <Box sx={{ pt: 2 }}>
              {/* NEW: Display Loan ID in dialog */}
              <Typography gutterBottom>
                <strong>Loan ID:</strong> #{currentLoan.id}
              </Typography>
              <Typography gutterBottom>
                <strong>Loan Amount:</strong> KES {currentLoan.amount.toLocaleString()}
              </Typography>
              <Typography gutterBottom>
                <strong>Installments:</strong> {currentLoan.installments}
              </Typography>
              {currentLoan.installment_amount && (
                <Typography gutterBottom>
                  <strong>Monthly Payment:</strong> KES {currentLoan.installment_amount.toLocaleString()}
                </Typography>
              )}
              {currentLoan.total_repayment && (
                <Typography gutterBottom>
                  <strong>Total Repayment (with 15% interest):</strong> KES {currentLoan.total_repayment.toLocaleString()}
                </Typography>
              )}
              <Typography gutterBottom sx={{ mb: 3 }}>
                <strong>Member:</strong> {currentLoan.member_name}
              </Typography>

              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={dialogAction === 'reject' || dialogAction === 'cancel' ? 'error' : 'primary'}
            onClick={handleAction}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoansInfo;